import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { isEmpty } from 'ramda';
import { isNilOrEmpty, isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import { getLabelMessage } from '@gef-ui/localization/messages';
import ArrowRight from '@gef-ui/material-icons/ArrowRight';
import FileExport from '@gef-ui/material-icons/FileExport';

import Subheader from 'components/atoms/GefMui/Subheader';
import ComponentLoader from 'components/molecules/ComponentLoader';
import MODALS from 'constants/modals';
import { KCL_DOCUMENT_CHECK_ADMINS, KCL_DOCUMENT_CHECK_MARPO } from 'features/common/acl/constants';
import { urls } from 'features/common/fetch/constants';
import {
	fetchClientsDocumentsToReview,
	fetchDocumentsToReview,
} from 'features/views/productionControl/documentsToReview/actions';
import { fetchClientsMyDocuments } from 'features/views/productionControl/myDocuments/actions';
import {
	fetchRegions,
	fetchSubordinates,
	fetchSuperiors,
	resetProductionControl,
	setOverviewByLastRequest,
	startupSelectionCriteria,
} from 'features/views/productionControl/selectionCriteria/actions';
import { FIELDS, SELECTION_CRITERIA_FORM_NAME } from 'features/views/productionControl/selectionCriteria/form';
import {
	getMyDocumentsTotalRows,
	getRegions,
	getSubordinates,
	getSuperiors,
	getUserIsSuperior,
	getUserIsSuperiorAndSubordinate,
	getWaves,
} from 'features/views/productionControl/selectors';
import {
	setShowMyDocuments,
	setUserIsSuperior,
	setUserIsSuperiorAndSubordinate,
} from 'features/views/productionControl/slice';
import { getDetailViewed } from 'features/views/productionControlDetail/selectors';

export const SelectionCriteria = ({ showMyDocuments, onReportConfirm }) => {
	const dispatch = useDispatch();
	const formData = useFormData();
	const next = useControlNext(SELECTION_CRITERIA_FORM_NAME);

	const documentCheckWaveCodelist = useSelector(getWaves);
	const regions = useSelector(getRegions);
	const superiors = useSelector(getSuperiors);
	const subordinates = useSelector(getSubordinates);

	const userIsCentral = useSelector((state) => hasPermissionByIds(state)(KCL_DOCUMENT_CHECK_ADMINS));
	const userIsMarpo = useSelector((state) => hasPermissionByIds(state)(KCL_DOCUMENT_CHECK_MARPO));
	const userIsSuperior = useSelector(getUserIsSuperior);
	const userIsSuperiorAndSubordinate = useSelector(getUserIsSuperiorAndSubordinate);
	const myDocumentsTotalRows = useSelector(getMyDocumentsTotalRows);

	const userIsCentralOrMarpo = userIsCentral || userIsMarpo;
	const formIsInitiated = isObject(formData) && isEmpty(formData);

	const detailViewed = useSelector(getDetailViewed);

	useEffect(() => {
		if (!detailViewed) {
			// init selection criteria with possible autofills
			if (!isNilOrEmpty(documentCheckWaveCodelist) && formIsInitiated) {
				dispatch(startupSelectionCriteria(documentCheckWaveCodelist[0]));
			}
		} else {
			formIsInitiated && dispatch(setOverviewByLastRequest());
		}
		// eslint-disable-next-line
	}, [dispatch, documentCheckWaveCodelist, formIsInitiated]);

	useEffect(() => {
		// fetching clientsMyDocuments ct only for superior/subordinate user
		if (!userIsCentralOrMarpo && !isNilOrEmpty(formData?.wave)) {
			dispatch(fetchClientsMyDocuments());
		}
	}, [dispatch, formData?.wave, userIsCentralOrMarpo]);

	useEffect(() => {
		// auto refetching > clients ct for documentsToReview
		if ((userIsCentralOrMarpo || userIsSuperior) && !isNilOrEmpty(formData?.wave)) {
			dispatch(fetchClientsDocumentsToReview());
		}
	}, [dispatch, formData, userIsCentralOrMarpo, userIsSuperior]);

	const handleReset = useCallback(() => {
		dispatch(resetProductionControl());
	}, [dispatch]);

	const handleSearch = useCallback(() => {
		dispatch(fetchDocumentsToReview());
	}, [dispatch]);

	const handleReport = useCallback(() => {
		if (userIsCentralOrMarpo) {
			NiceModal.show(MODALS.productionControl_report);
		} else {
			dispatch(onReportConfirm());
		}
	}, [dispatch, userIsCentralOrMarpo, onReportConfirm]);

	const handleToggleShowMyDocuments = useCallback(() => {
		dispatch(setShowMyDocuments(!showMyDocuments));
	}, [dispatch, showMyDocuments]);

	const isRegionActive = (userIsCentral, userIsMarpo, regions, wave) => {
		if (isNilOrEmpty(wave)) {
			return false;
		} else {
			if (userIsCentral) {
				return true;
			}

			if (userIsMarpo) {
				return regions.length !== 1;
			}
		}
	};

	// resetting possible previous values
	const resetFields = useCallback(
		(level) => {
			const fields = ['wave', 'region', 'superior', 'subordinate'];
			const fieldsToBeReseted = fields.slice(level);

			fieldsToBeReseted.forEach((fieldName) => next(setValue({ fieldName, value: null })));
		},
		[next],
	);

	const handleWaveChange = useCallback(
		({ level }) =>
			() => {
				resetFields(level);
				dispatch(setShowMyDocuments(false));
				if (!userIsCentralOrMarpo) {
					// user could have been superior in previosly selected wave
					// quick reset to non superior status
					dispatch(setUserIsSuperior(false));
					dispatch(setUserIsSuperiorAndSubordinate(false));
					dispatch(fetchSubordinates());
				} else {
					dispatch(fetchRegions());
				}
			},
		[dispatch, userIsCentralOrMarpo, resetFields],
	);

	const handleRegionChange = useCallback(
		({ level }) =>
			() => {
				resetFields(level);
				dispatch(fetchSuperiors());
			},
		[dispatch, resetFields],
	);

	const handleSuperiorChange = useCallback(
		({ level }) =>
			() => {
				resetFields(level);
				dispatch(fetchSubordinates());
			},
		[dispatch, resetFields],
	);

	const handleSubordinateChange = useCallback(() => {
		// onChange refetch documentsToReview only for superior subordinates
		if (!userIsCentralOrMarpo && userIsSuperior) {
			dispatch(fetchDocumentsToReview());
		}
	}, [dispatch, userIsCentralOrMarpo, userIsSuperior]);

	const showReportButton =
		userIsCentralOrMarpo || (!userIsCentralOrMarpo && !isNilOrEmpty(formData?.wave) && userIsSuperior);

	const subheaderToolbar = showReportButton && (
		<Button variant="text" size="small" startIcon={<FileExport />} onClick={handleReport}>
			{getLabelMessage('productionControl:report')}
		</Button>
	);

	return (
		<Paper sx={{ p: 3, borderRadius: 3 }}>
			<Stack spacing={2}>
				<Subheader
					title={getLabelMessage('productionControl:productionControlSelectionCriteria')}
					headerToolbar={subheaderToolbar}
				/>
				<Grid container>
					<Grid size={12}>
						<Grid container spacing={2}>
							<Grid size={6}>
								<ComponentLoader fetchStorePath={`${urls.codeTable}.DOCUMENT_CHECK_WAVE`}>
									<Autocomplete
										size="small"
										fieldName={FIELDS.wave}
										label={getLabelMessage('productionControl:wave')}
										options={documentCheckWaveCodelist}
										labelPropName="description"
										onChange={handleWaveChange({ level: 1 })}
										disableClearable
									/>
								</ComponentLoader>
							</Grid>
							{userIsCentralOrMarpo && (
								<>
									<Grid size={6}>
										<ComponentLoader fetchStorePath={urls.koprRegion}>
											<Autocomplete
												size="small"
												fieldName={FIELDS.region}
												label={getLabelMessage('productionControl:region')}
												options={regions}
												disabled={
													!isRegionActive(userIsCentral, userIsMarpo, regions, formData?.wave)
												}
												labelPropName="description"
												onChange={handleRegionChange({ level: 2 })}
											/>
										</ComponentLoader>
									</Grid>
									<Grid size={6}>
										<ComponentLoader fetchStorePath={urls.koprSuperiors}>
											<Autocomplete
												size="small"
												fieldName={FIELDS.superior}
												label={getLabelMessage('productionControl:superior')}
												options={superiors}
												disabled={isNilOrEmpty(formData?.region)}
												labelPropName="description"
												onChange={handleSuperiorChange({ level: 3 })}
											/>
										</ComponentLoader>
									</Grid>
								</>
							)}

							{(userIsCentralOrMarpo || userIsSuperior) && (
								<Grid size={6}>
									<ComponentLoader fetchStorePath={urls.koprSubordinates}>
										<Autocomplete
											size="small"
											fieldName={FIELDS.subordinate}
											label={getLabelMessage('productionControl:subordinate')}
											options={subordinates}
											disabled={
												userIsCentralOrMarpo
													? isNilOrEmpty(formData?.superior)
													: isNilOrEmpty(formData?.wave)
											}
											labelPropName="description"
											onChange={handleSubordinateChange}
										/>
									</ComponentLoader>
								</Grid>
							)}
							{userIsCentralOrMarpo && (
								<Grid size={12}>
									<Grid
										container
										spacing={2}
										sx={{
											justifyContent: 'flex-end',
										}}
									>
										<Grid size={2}>
											<Button variant="outlined" size="small" fullWidth onClick={handleReset}>
												{getLabelMessage('productionControl:resetCriteria')}
											</Button>
										</Grid>
										<Grid size={2}>
											<Button variant="contained" size="small" fullWidth onClick={handleSearch}>
												{getLabelMessage('productionControl:search')}
											</Button>
										</Grid>
									</Grid>
								</Grid>
							)}
							{userIsSuperiorAndSubordinate && myDocumentsTotalRows > 0 && (
								<Grid size={12}>
									<Alert
										severity="warning"
										action={
											<Button
												onClick={handleToggleShowMyDocuments}
												sx={{ color: 'primary.main' }}
												variant="outlined"
												endIcon={<ArrowRight />}
											>
												{getLabelMessage(`common:${showMyDocuments ? 'wrapUp' : 'unwrap'}`)}
											</Button>
										}
									>
										{getLabelMessage('productionControl:undeliveredDocumentsWarning')}
										{`(${getLabelMessage(
											'productionControl:documentsSum',
										)} ${myDocumentsTotalRows})`}
									</Alert>
								</Grid>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Stack>
		</Paper>
	);
};

SelectionCriteria.propTypes = {
	onReportConfirm: PropTypes.func,
	showMyDocuments: PropTypes.bool,
};

export default SelectionCriteria;
